import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Globe, Database, Users, Sparkles } from "lucide-react";
import Seo from "@/components/Seo";

export default function About() {
  const { i18n } = useTranslation();
  const isFr = (i18n.language || "en").toLowerCase().startsWith("fr");

  const content = isFr
    ? {
        eyebrow: "// NOTRE MISSION",
        title: "Indexer chaque carte Pokémon, dans chaque langue, au prix réel.",
        subtitle:
          "Cartonomie est né d'un constat simple : les prix des cartes Pokémon varient énormément selon la langue, le pays et la plateforme — et aucun outil ne les compare proprement. Nous construisons l'index mondial qui manquait à la communauté.",
        story_title: "L'histoire en deux phrases",
        story_body:
          "Cartonomie est un projet indépendant lancé par un collectionneur passionné, frustré de devoir ouvrir cinq onglets pour comparer le prix d'une carte japonaise à sa version française. Aujourd'hui, ce sont des milliers de cartes suivies en continu sur 8 langues, agrégeant des ventes réelles eBay, Cardmarket, TCGPlayer et Mercari.",
        values_title: "Ce qui nous guide",
        values: [
          {
            icon: Globe,
            title: "Multi-langue par essence",
            desc: "Comparer une carte JP, FR, EN ou DE doit prendre 2 secondes. Pas 2 heures."
          },
          {
            icon: Database,
            title: "Données réelles uniquement",
            desc: "Pas de prix gonflés par les vendeurs. On agrège les ventes effectives, déduplique et nettoie les anomalies."
          },
          {
            icon: Users,
            title: "Construit avec la communauté",
            desc: "Vous pouvez contribuer des prix de vente pour enrichir l'index. Plus on est, plus c'est précis."
          },
          {
            icon: Sparkles,
            title: "Indépendant et sans pub",
            desc: "Aucune publicité, aucun tracking marketing. Le Premium finance le projet, point."
          },
        ],
        cta_title: "Rejoindre l'index",
        cta_body:
          "Créez un compte gratuit pour suivre votre collection, recevoir des alertes de prix et contribuer aux données.",
        cta_btn: "Créer un compte",
        cta_secondary: "Nous contacter",
      }
    : {
        eyebrow: "// OUR MISSION",
        title: "Index every Pokémon card, in every language, at real market price.",
        subtitle:
          "Pokémon card prices vary wildly across languages, regions and platforms — and no tool compares them properly. Cartonomie builds the global index the community was missing.",
        story_title: "The story in two sentences",
        story_body:
          "Cartonomie was launched by an independent collector tired of opening five tabs just to compare a Japanese card to its French counterpart. Today we track thousands of cards across 8 languages, aggregating real sales from eBay, Cardmarket, TCGPlayer and Mercari.",
        values_title: "What guides us",
        values: [
          { icon: Globe, title: "Multilingual by design", desc: "Comparing JP, FR, EN or DE prints should take 2 seconds." },
          { icon: Database, title: "Real data only", desc: "No seller-inflated listings. We aggregate completed sales, dedupe and filter anomalies." },
          { icon: Users, title: "Community-built", desc: "Submit your own sales to feed the index. The bigger the dataset, the sharper the prices." },
          { icon: Sparkles, title: "Independent, ad-free", desc: "Zero ads, no marketing tracking. Premium subscriptions fund the project." },
        ],
        cta_title: "Join the index",
        cta_body: "Create a free account to track your collection, set price alerts and contribute data.",
        cta_btn: "Create account",
        cta_secondary: "Contact us",
      };

  return (
    <div className="min-h-screen bg-white" data-testid="about-page">
      <Seo title={isFr ? "À propos de Cartonomie" : "About Cartonomie"} description={content.subtitle} />

      <section className="max-w-4xl mx-auto px-6 lg:px-10 pt-16 pb-12">
        <span className="text-[10px] tracking-[0.18em] text-slate-400 font-semibold">{content.eyebrow}</span>
        <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight text-slate-900 mt-3">
          {content.title}
        </h1>
        <p className="text-base text-slate-600 leading-relaxed mt-6 max-w-2xl">{content.subtitle}</p>
      </section>

      <section className="max-w-4xl mx-auto px-6 lg:px-10 py-12 border-t border-slate-100">
        <h2 className="font-heading font-bold text-sm uppercase tracking-wider text-slate-400 mb-3">
          {content.story_title}
        </h2>
        <p className="text-lg text-slate-800 leading-relaxed font-medium">{content.story_body}</p>
      </section>

      <section className="max-w-5xl mx-auto px-6 lg:px-10 py-12 border-t border-slate-100">
        <h2 className="font-heading font-bold text-2xl text-slate-900 mb-8">{content.values_title}</h2>
        <div className="grid sm:grid-cols-2 gap-6">
          {content.values.map(({ icon: Icon, title, desc }) => (
            <div key={title} className="p-6 rounded-2xl border border-slate-200 hover:border-[#E11D48] transition group">
              <div className="w-10 h-10 rounded-xl bg-slate-900 text-white flex items-center justify-center mb-4 group-hover:bg-[#E11D48] transition">
                <Icon className="w-5 h-5" />
              </div>
              <h3 className="font-heading font-bold text-base text-slate-900 mb-2">{title}</h3>
              <p className="text-sm text-slate-600 leading-relaxed">{desc}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="max-w-4xl mx-auto px-6 lg:px-10 py-16 border-t border-slate-100 text-center">
        <h2 className="font-heading font-extrabold text-3xl sm:text-4xl text-slate-900 mb-4">
          {content.cta_title}
        </h2>
        <p className="text-sm text-slate-600 max-w-xl mx-auto mb-8">{content.cta_body}</p>
        <div className="flex flex-wrap justify-center gap-3">
          <Link to="/register" data-testid="about-cta-register" className="btn-primary">
            {content.cta_btn}
          </Link>
          <Link to="/contact" data-testid="about-cta-contact" className="px-5 py-2.5 text-sm font-semibold rounded-xl border border-slate-300 text-slate-700 hover:border-[#E11D48] hover:text-[#E11D48] transition">
            {content.cta_secondary}
          </Link>
        </div>
      </section>
    </div>
  );
}
